import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useActiveApplicationIdentifier } from './useActiveApplicationIdentifier';

const STALE_TIME_MS = 5 * 60 * 1000;

/** Stable, so a `PolicyProvider` doesn't rebuild its evaluator on every render while nothing is loaded. */
const NO_ACTIONS: readonly string[] = [];

/**
 * The actions the signed-in user may take in the active brand and application, resolved by the
 * API from their own policies plus those of their role for that application. Feed the result to a
 * `PolicyProvider`; the client never sees or evaluates the policies themselves.
 *
 * The request is scoped by the active brand and application headers, so the scope is not part of
 * the cache key: `ActiveScopeSync` resets this query (it is deliberately not on its whitelist) when
 * the scope changes, which refetches it. No previous data is kept while it does (no placeholder or
 * keep-previous-data), since the previous application's role may allow more than the new one's.
 * Until the actions arrive, or if they can't be loaded, this returns none, so everything gated is
 * denied. UI gating only; the API enforces access on every request.
 *
 * @param userIdentifier The signed-in user. Part of the cache key, so one user's actions are
 *   never served to another. The query waits until it is known.
 */
export function useApplicationAllowedActions(userIdentifier: string | null): readonly string[] {
  const apiClient = useChatApiClient();
  const brandIdentifier = useActiveBrandIdentifier();
  const applicationIdentifier = useActiveApplicationIdentifier();

  const query = useQuery({
    queryKey: ['get:user-allowed-actions', userIdentifier],
    queryFn: async () => {
      return (await apiClient.users.getAllowedActions()).allowedActions;
    },
    enabled: !!userIdentifier && !!brandIdentifier && !!applicationIdentifier,
    staleTime: STALE_TIME_MS,
  });

  return query.data ?? NO_ACTIONS;
}
